import { Divider } from "@mui/material";
import i18next from "i18next";
import React, { useEffect } from "react";

import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router";
import Button from "../../components/Button";
import Label from "../../components/Label";
import Loader from "../../components/Loader";
import SimpleDialog from "../../components/SimpleDialog";
import Table from "../../components/Table";
import * as gendersServices from "../../services/gendersServices";
import * as samplesServices from "../../services/samplesServices";
import { routes } from "../../utils";
import { Sample } from "../../utils/types";
import AgeSelect from "../selects/AgeSelect";
import BasinSelect from "../selects/BasinSelect";
import CategorySelect from "../selects/CategorySelect";
import ExtractionSelect from "../selects/ExtractionSelect";
import FormationSelect from "../selects/FormationSelect";
import GenderSelect from "../selects/GenderSelect";
import { SampleListFilterContainer } from "../styles";

type Params = {
	search: string;
	skip: number;
	limit: number;
	date_from: string;
	date_to: string;
	extractions: any[];
	ages: any[];
	basins: any[];
	genders: any[];
	formations: any[];
};

const columns = ["preparation-number", "age", "gender", "formation", "extraction", "basins"];

const INITIAL_PARAMS = {
	skip: 0,
	limit: 10,
	date_from: null,
	date_to: null,
	extractions: [],
	ages: [],
	basins: [],
	genders: [],
	formations: [],
	categories: [],
};

export default function SamplesList() {
	const [data, setData] = React.useState<{ result: Sample[]; total: number }>({ result: [], total: 0 });
	const [loading, setLoading] = React.useState<boolean>(false);
	const [genderOptions, setGenderOptions] = React.useState<any>();
	const [params, setParams] = React.useState<any>(INITIAL_PARAMS);

	const navigate = useNavigate();

	const goBack = () => navigate(routes.DASHBOARD);

	useEffect(() => {
		getSamples(params);
	}, [params]);

	const getSamples = async (params: Params) => {
		setLoading(true);
		const data = await handleFilter(params);
		setData({
			result: data.result.map((sample: Sample) => ({
				age: sample.age ? sample.age.name : "-",
				formation: sample.formation ? sample.formation.name : "-",
				"preparation-number": sample?.preparation_numbers?.length
					? sample.preparation_numbers.map((pN) => pN).join(", ")
					: "-",
				extraction: sample.extraction?.code,
				gender: sample.genders?.length > 0 ? sample.genders.map(({ name }) => name).join(", ") : "-",
				basins: sample.basins?.length > 0 ? sample.basins.map(({ name }) => name).join(", ") : "-",
				id: sample.id,
			})),
			total: data.total,
		});
		setLoading(false);
	};

	const handleUpdate = (options: any) => getSamples({ ...params, ...options });

	const handleFilter = async (params: Params) => samplesServices.search(params);

	return (
		<SimpleDialog open={true} onClose={goBack}>
			<Label isTitle titleCassName="mb-3" text={i18next.t("samples")} />
			<Table
				style={{ width: 800 }}
				onRowPress={(sample) => navigate(routes.SAMPLE(sample.id))}
				keyExtractor={(row) => row?.id}
				columns={columns}
				rows={data.result}
				total={data.total}
				handleUpdate={handleUpdate}
				filtersComponents={
					<SampleListFilterContainer>
						<Row className="w-100" data-testid="sample-list-filter">
							<Col sm={6}>
								<FormationSelect
									multiple={true}
									value={params.formations}
									onChange={(formations) => setParams({ ...params, formations })}
								/>
							</Col>
							<Col sm={6}>
								<BasinSelect
									multiple={true}
									value={params.basins}
									onChange={(basins) => setParams({ ...params, basins })}
								/>
							</Col>
							<Col sm={6}>
								<AgeSelect
									multiple={true}
									value={params.ages}
									onChange={(ages) => setParams({ ...params, ages })}
								/>
							</Col>
							<Col sm={6}>
								<ExtractionSelect
									multiple={true}
									value={params.extractions}
									onChange={(extractions) => setParams({ ...params, extractions })}
								/>
							</Col>
							<Col sm={6}>
								<CategorySelect
									multiple={true}
									value={params.categories}
									onChange={(categories) => {
										if (categories.length === 0) {
											setGenderOptions(undefined);
											setParams({
												...params,
												categories: [],
											});
										} else {
											gendersServices
												.search({
													search: "",
													categories: categories.map((category) => category.id),
												})
												.then((res) => {
													setParams({
														...params,
														genders: params.genders.filter((g) =>
															res.result.map((a) => a.id).includes(g.id)
														),
														categories,
													});
													setGenderOptions(res.result);
												});
										}
									}}
								/>
							</Col>
							<Col sm={6}>
								<GenderSelect
									multiple={true}
									value={params.genders}
									categories={params.categories.map((category) => category.id)}
									options={genderOptions}
									onChange={(genders) => setParams({ ...params, genders })}
								/>
							</Col>
						</Row>
						<Divider className="mt-3 mb-2" />
					</SampleListFilterContainer>
				}
			/>
			<div className="d-flex mt-4 justify-content-between">
				{loading ? <Loader /> : <div />}
				<Button
					label={i18next.t("go-back")}
					datatestid="cancel-button"
					secondary
					onClick={goBack}
					className="ms-4"
				/>
			</div>
		</SimpleDialog>
	);
}
